// Utilities for working with potential bidders.
// Pass tenderSourceId (e.g. tender.source.id) to check platform support.

const COMPLETED_TENDER_STATUS = 'complete';

// TODO: Add a platform (tenderSourceId) support helper here when platform capabilities are defined.

/**
 * Normalizes an organization name for comparison between potential bidders
 * and actual bidders (the backend source of truth does not yet expose a
 * shared identifier).
 *
 * Mirrors the backend `normalize_str` helper exactly: lowercase, decompose with
 * NFKD, drop accents (combining marks), then collapse anything that is not a-z
 * or 0-9. Keep this in sync with the backend so both sides produce the same key.
 * @param {string} name
 * @returns {string}
 */
export function getBidderMatchKey(name) {
  if (typeof name !== 'string') return '';
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/\p{Mn}/gu, '')
    .replace(/[^a-z0-9]+/g, '');
}

/**
 * Returns true if a potential bidder appears in the list of received bids
 * (matched by normalized name).
 * @param {Object} potentialBidder
 * @param {Array} bids
 * @returns {boolean}
 */
export function isPotentialBidderSubmitted(potentialBidder, bids) {
  if (!potentialBidder || !Array.isArray(bids) || bids.length === 0) return false;
  const key = getBidderMatchKey(potentialBidder.organization_name);
  if (!key) return false;
  return bids.some((bid) => getBidderMatchKey(bid.supplier_name) === key);
}

/**
 * Computes how many potential bidders actually submitted a bid.
 * @param {Array} potentialBidders
 * @param {Array} bids
 * @returns {{ submitted: number, total: number, ratio: number }}
 */
export function computeSubmissionRatio(potentialBidders, bids) {
  const total = Array.isArray(potentialBidders) ? potentialBidders.length : 0;
  if (total === 0) return { submitted: 0, total: 0, ratio: 0 };
  const submitted = potentialBidders.reduce((acc, pb) => (isPotentialBidderSubmitted(pb, bids) ? acc + 1 : acc), 0);
  return { submitted, total, ratio: submitted / total };
}

/**
 * Returns true if the tender status is closed/completed.
 * Submission ratios only make sense after the tender has closed.
 * @param {string} status
 * @returns {boolean}
 */
export function isTenderClosed(status) {
  return [COMPLETED_TENDER_STATUS, 'canceled', 'withdrawn', 'unsuccessful'].includes(status);
}
